import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router, RouterLink } from "@angular/router";
import {
  AuthService,
  BillingService,
  OrganizationService,
  RoutingService,
  UserSelectionService,
} from "@front/m19-services";
import {
  IAccordionComponent,
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ITooltipDirective,
  SidebarItemComponent,
  SidebarLayoutComponent,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { NavItem } from "@m19-board/containers/board-layout/board-sidebar/nav-item";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { EnvironmentService } from "@m19-board/services/environment.service";
import { LogoComponent } from "@m19-board/shared/logo/logo.component";
import { map } from "rxjs/operators";
import { OnboardingCtaComponent } from "./onboarding-cta.component";
import { findBestMatchingNavItem, SidebarItemFactory } from "./sidebar-items";

@Component({
  selector: "app-board-sidebar",
  imports: [
    SidebarLayoutComponent,
    SidebarItemComponent,
    IAccordionComponent,
    IButtonDirective,
    ITooltipDirective,
    TranslocoDirective,
    RouterLink,
    OnboardingCtaComponent,
    LogoComponent,
    IDropdownMenuTriggerDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuSeparatorComponent,
    IIconComponent,
  ],
  templateUrl: "./board-sidebar.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoardSidebarComponent implements OnInit {
  router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly layoutSelector = inject(LayoutSelectorService);
  private readonly billingService = inject(BillingService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly routingService = inject(RoutingService);
  private readonly organizationsService = inject(OrganizationService);

  readonly isAdminLogin = this.authService.isAdminLogin();
  readonly environment = this.environmentService.getEnvironmentConfig();
  readonly releaseNotesUrl = this.environmentService.getEnvironmentConfig().releaseNotesUrl;
  readonly BoardType = BoardType;
  readonly settingsItem = { label: "page-title.settings", icon: "gear" as const, id: "settings" };

  readonly productTour = toSignal(this.authService.isProductTour$, { initialValue: false });
  readonly hasBillingAccess = toSignal(
    this.billingService.organizationsWithBillingAccess$.pipe(map((o) => o.length > 0)),
  );
  isCollapsed = this.userSelectionService.sidebarCollapsed;
  isBetaTester = toSignal<boolean>(this.authService.loggedUser$.pipe(map((u) => !!u.betaTester)), {
    requireSync: true,
  });
  organizations = toSignal(this.organizationsService.organizations$);
  hasAccessToDataWarehouse = computed(() => this.organizations()?.some((o) => o.dataWarehouseAccess));

  currentPath = toSignal(this.routingService.currentFullPath$);
  openedItemId = signal<string | undefined>(undefined);
  selectedItemId = computed(() => {
    if (!this.currentPath()) return undefined;

    const bestMatchingNavItem = findBestMatchingNavItem(this.menu(), this.currentPath()!);
    return bestMatchingNavItem?.id;
  });

  boardType = this.layoutSelector.getBoardType();
  whitelabelContactUsLink = this.layoutSelector.getContact();
  readonly helpItem = {
    label: "page-title.help",
    icon: "helpCircle" as const,
    href: this.boardType === BoardType.M19 ? "https://help.m19.com/" : this.whitelabelContactUsLink,
    id: "help",
  };

  menu = computed<NavItem[]>(() => this.getNavItems());

  constructor() {
    effect(() => {
      localStorage.setItem("sidebar-opened-item", this.openedItemId() ?? "");
      localStorage.setItem("sidebar-collapsed", this.isCollapsed().toString());
    });
  }

  ngOnInit(): void {
    this.openedItemId.set(localStorage.getItem("sidebar-opened-item") ?? undefined);
  }

  getNavItems() {
    return new SidebarItemFactory().getNavItems(this.isBetaTester(), this.isAdminLogin, this.environment.environment);
  }

  logout() {
    this.authService.logout();
  }

  toggleItem(id: string) {
    this.openedItemId.set(this.openedItemId() === id ? undefined : id);
  }
}
